/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { memo, useCallback, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	type EmptyChatLaunchProjectContext,
	useSideNavUISelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { DeleteConversationModal } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list/DeleteConversationModal';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';
import { RovoSideNavigation as SideNavigation } from '@atlassian/rovo-navigation/side-navigation';
import { buildSpaceStarters } from '@atlassian/rovo-spaces/common/starters';
import type { CreateSpaceResponse } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { CreateSpaceModal } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';
import type { CreateSpacePayload } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useRovoNavigation } from '../../controllers/rovo-navigation';
import { ConversationAssistantUIModes } from '../../types';
import { SUGGESTIONS_TAB_ID } from '../chat-history/rovo-tabs/constants';

type RovoSideNavigationProps = {
	isOpen: boolean;
	onClose: () => void;
	chatMode: ConversationAssistantUIModes;
	/** Called after a space is successfully created. */
	onSpaceCreated?: (
		space: CreateSpaceResponse,
		sources: Array<{ ari: string; url: string; sourceType: string; name?: string }>,
		emojiId?: string,
	) => void;
	/**
	 * Optional host callback fired when the user clicks the "View all Rovo spaces"
	 * link in the side nav. When omitted the link is hidden, so existing hosts
	 * that have not opted in are unaffected. Mirrors the existing
	 * `onViewAllAgentsClick` host wiring.
	 */
	onViewAllSpacesClick?: () => void;
	/** Opt out of the in-package `SpaceLandingPage` for hosts that render their own. */
	disableInPackageSpaceLandingPage?: boolean;
};

type RovoSideNavigationInnerProps = RovoSideNavigationProps & {
	activeMenu: ReturnType<typeof useSideNavUISelector>[0]['activeMenu'];
	selectedSpace: ReturnType<typeof useSideNavUISelector>[0]['selectedSpace'];
};

const RovoSideNavigationInner = ({
	isOpen,
	onClose,
	chatMode,
	onSpaceCreated,
	onViewAllSpacesClick,
	disableInPackageSpaceLandingPage = false,
	activeMenu,
	selectedSpace,
}: RovoSideNavigationInnerProps): JSX.Element => {
	di(useInsightsStatus);
	const { fullScreen } = useUIConfig();
	const { profileData } = useGlobalConfig();
	const touchpointSource = 'rovo-sidenav';
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource,
	});
	const { sendMessage, setSelectedConversation } = useChatStoreActions();

	const TASKS_ENABLED = fg('rovo_chat_enable_tasks');
	const PINNED_CHATS_ENABLED = fg('rovo_chat_pinned_fe');

	const {
		siteId,
		agents,
		selectedAgent,
		conversations,
		pinnedCountConversations,
		pinnedConversationSummary,
		currentConversation,
		isAbleToCreateAgents,
		isFetchingRecommendedAgents,
		handleNewChatClick,
		handleConversationClick,
		handleCreateAgentClick,
		handleSelectAgent,
		handleViewAllAgentsClick,
		handleViewAllSpacesClick,
		handleViewAllConversationsClick,
		handlePulseClick,
		handleTasksClick,
		handleScheduledChatsClick,
		handleConversationStarterClick,
		handleConversationPinToggle,
		deleteAgentLocally,
		deleteConversation: { conversationToBeDeleted, openDeleteModal, handleDeleteConfirm },
		editConversation: { handleEditConversationConfirm },
		moveToSpace: {
			handleMoveConversationToSpace,
			handleMoveConversationToNewSpace,
			conversationToMoveToNewSpace,
			clearConversationToMoveToNewSpace,
		},
	} = useRovoNavigation({
		assistanceServiceParams,
		onConversationStarterClick: (params) => {
			sendMessage(params);
		},
		isTasksEnabled: TASKS_ENABLED,
		isNavOpen: isOpen,
		onViewAllSpacesClick,
	});

	const mode =
		fullScreen || chatMode === ConversationAssistantUIModes.FULL_SCREEN_MODAL
			? 'fullscreen'
			: chatMode;
	const { setActiveMenu, setSelectedSpace, setActiveTabId, setEmptyChatLaunch } =
		useUIStoreActions();
	const { setAIFeatureContext } = useChatContextStoreActions();
	const intl = useIntl();

	// Only enabled when ADS navigation should be used (only true for townsquare which uses rovo-navigation directly)
	const isADSNavigationEnabled = false;

	const SCHEDULED_CHATS_ENABLED = FeatureGates.getExperimentValue(
		'rovo_chat_scheduled_chat',
		'isEnabled',
		false,
	);

	// Check if pulse experiment is enabled (exclude fullScreen, matching the empty state banner behavior)
	const isPulseExperimentEnabled =
		!fullScreen && FeatureGates.getExperimentValue('rovo_growth_chat_pulse', 'isEnabled', false);

	const { isInsightsAvailable } = useInsightsStatus({
		enabled: isPulseExperimentEnabled,
		assistanceServiceParams,
	});

	const showInsightsMenu = isPulseExperimentEnabled && isInsightsAvailable;

	const isMoveConversationCreateSpaceModalOpen = conversationToMoveToNewSpace !== undefined;
	const [isStandaloneCreateSpaceModalOpen, setIsStandaloneCreateSpaceModalOpen] = useState(false);

	// Capture sources from onCreate (fires locally before BE call) so we can
	// include them in the store when onCreated fires (after BE confirms creation).
	const pendingSourcesRef = useRef<
		Array<{ ari: string; url: string; sourceType: string; name?: string }>
	>([]);

	const handleCreate = useCallback((payload: CreateSpacePayload) => {
		pendingSourcesRef.current = (payload.sources ?? []).map((s) => ({
			ari: s.ari,
			url: s.url,
			sourceType: s.sourceType,
			name: s.name,
		}));
	}, []);

	const handleSelectSpace = useCallback(
		(
			space: { id: string; name: string; emoji?: string | null },
			sources?: Array<{ ari: string; url: string; sourceType: string; name?: string }>,
			closeNav?: boolean,
		) => {
			setSelectedSpace({
				id: space.id,
				name: space.name,
				emoji: space.emoji,
				sources,
				entryPoint: 'sidebar',
			});
			// Switch to suggestions tab in case the user was on the For You tab,
			// which would otherwise hide the chat input via MiniModalForYouThreadsListHidesInput.
			setActiveTabId(SUGGESTIONS_TAB_ID);
			setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
			// Replace any default conversation actions with the shared space
			// starters so the empty chat shows space-relevant suggestions.
			// This is gated by the Rovo spaces experiment (matching the rest of the Spaces UI)
			// for defense-in-depth, even though `handleSelectSpace` itself is only reachable
			// from gated entry points.
			if (FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)) {
				// Build a project context so that the resulting conversation is
				// scoped to this space. Without this, sendMessage would resolve
				// `spaceId` from `aiFeatureContext.projectContext.spaceId` (see
				// `send-message-action.ts`) and find nothing — meaning the new
				// conversation would not be created in the selected space.
				// Mirrors the wiring used by `handleChatEmptyLaunch` in
				// `chat-history/index.tsx` (which is invoked from the For You
				// space cards) so both entry points behave identically.
				const projectContext: EmptyChatLaunchProjectContext = {
					projectId: space.id,
					projectName: space.name,
					projectUrl: '',
					spaceId: space.id,
				};

				// Write project context into the chat-context store so that
				// sendMessage includes `space_id` in the backend request payload
				// for any message launched from this empty chat (including the
				// space starter prompts below).
				setAIFeatureContext({
					contextKey: 'projectContext',
					setContext: <T,>(_: T): T => projectContext as T,
				});

				setEmptyChatLaunch({
					// Build the starters imperatively here so the freshly-selected
					// space's name is interpolated into the prompts (rather than
					// the previously-selected space, which is what `selectedSpace`
					// would still hold at this point).
					customConversationActions: buildSpaceStarters(intl, { spaceName: space.name }),
					replaceProviderActions: true,
					projectContext,
				});
			}
			if (
				!disableInPackageSpaceLandingPage &&
				chatMode !== ConversationAssistantUIModes.MINI_MODAL &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
			) {
				setActiveMenu('space-landing');
			} else if (activeMenu !== 'none') {
				setActiveMenu('none');
			}
			if (closeNav) {
				onClose();
			}
		},
		[
			setSelectedSpace,
			setActiveTabId,
			setSelectedConversation,
			setAIFeatureContext,
			setEmptyChatLaunch,
			intl,
			activeMenu,
			chatMode,
			setActiveMenu,
			disableInPackageSpaceLandingPage,
			onClose,
		],
	);

	return (
		<>
			{isMoveConversationCreateSpaceModalOpen && (
				<CreateSpaceModal
					isOpen={isMoveConversationCreateSpaceModalOpen}
					onClose={clearConversationToMoveToNewSpace}
					cloudId={siteId}
					assistanceServiceParams={assistanceServiceParams}
					initialConversationId={conversationToMoveToNewSpace?.id}
					onCreate={handleCreate}
					onCreated={(space, emojiId) => {
						// Do NOT call `addSpace` here — the host's `onSpaceCreated` callback owns the optimistic update.
						const sources = pendingSourcesRef.current;
						clearConversationToMoveToNewSpace();
						handleSelectSpace(
							{ id: space.id, name: space.name, emoji: emojiId ?? null },
							sources,
							true,
						);
						onSpaceCreated?.(space, sources, emojiId);
						pendingSourcesRef.current = [];
					}}
				/>
			)}
			{isStandaloneCreateSpaceModalOpen && (
				<CreateSpaceModal
					isOpen={isStandaloneCreateSpaceModalOpen}
					onClose={() => setIsStandaloneCreateSpaceModalOpen(false)}
					cloudId={siteId}
					assistanceServiceParams={assistanceServiceParams}
					onCreate={handleCreate}
					onCreated={(space, emojiId) => {
						// See the move-conversation modal above — host owns
						// the optimistic add via `onSpaceCreated`.
						const sources = pendingSourcesRef.current;
						setIsStandaloneCreateSpaceModalOpen(false);
						handleSelectSpace(
							{ id: space.id, name: space.name, emoji: emojiId ?? null },
							sources,
							true,
						);
						onSpaceCreated?.(space, sources, emojiId);
						pendingSourcesRef.current = [];
					}}
				/>
			)}
			<SideNavigation
				cloudId={siteId}
				userId={profileData.aaid ?? undefined}
				assistanceServiceParams={assistanceServiceParams}
				mode={mode}
				isADSNavigationEnabled={isADSNavigationEnabled}
				onCreateSpaceClick={() => setIsStandaloneCreateSpaceModalOpen(true)}
				onViewAllSpacesClick={
					// Show the "View all Rovo spaces" link whenever Rovo spaces is enabled: the controller
					// handler defaults to flipping the
					// in-app activeMenu to `'browse-spaces'`, and additionally
					// invokes the optional host callback if one is provided.
					// Mirrors the agents equivalent but does not require host
					// wiring to surface a destination.
					FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
						? handleViewAllSpacesClick
						: undefined
				}
				onSelectRecommendedSpaces={() => {
					setActiveMenu('recommended-spaces');
					setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
				}}
				onSelectSpace={handleSelectSpace}
				selectedSpaceId={
					selectedSpace?.id ?? (activeMenu === 'recommended-spaces' ? 'recommended' : undefined)
				}
				isNewAllConversationScreenEnabled={fg('rovo_chat_chat_history_refresh')}
				isSpinnerOverlayConversationIconEnabled={fg('rovo_chat_chat_history_refresh')}
				isOpen={isOpen}
				onClose={onClose}
				canCreateAgents={isAbleToCreateAgents}
				onPulseClick={showInsightsMenu ? handlePulseClick : undefined}
				onTasksClick={TASKS_ENABLED ? handleTasksClick : undefined}
				onScheduledChatsClick={SCHEDULED_CHATS_ENABLED ? handleScheduledChatsClick : undefined}
				isTasksEnabled={TASKS_ENABLED}
				conversationListProps={{
					conversations,
					pinnedCountConversations,
					pinnedConversationSummary,
					currentConversation,
					onSelectConversation: handleConversationClick,
					onConversationDelete: openDeleteModal,
					onConversationRename: handleEditConversationConfirm,
					onConversationPinToggle: PINNED_CHATS_ENABLED ? handleConversationPinToggle : undefined,
					onViewAllConversations: handleViewAllConversationsClick,
					onConversationMoveToSpace: handleMoveConversationToSpace,
					onConversationMoveToNewSpace: handleMoveConversationToNewSpace,
				}}
				onNewChatButtonClick={() => handleNewChatClick(isADSNavigationEnabled)}
				agentsListProps={{
					agents,
					isFetchingRecommendedAgents,
					onSelectAgent: handleSelectAgent,
					onCreateAgentClick: handleCreateAgentClick,
					onViewAllAgentsClick: handleViewAllAgentsClick,
					selectedAgent: selectedAgent,
					onConversationStarterClick: handleConversationStarterClick,
					onDeleteAgent: deleteAgentLocally,
				}}
			/>

			{conversationToBeDeleted && (
				<DeleteConversationModal
					onDeleteConfirm={() => handleDeleteConfirm(conversationToBeDeleted.id)}
					analyticsAttributes={{ touchpointSource }}
				/>
			)}
		</>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedRovoSideNavigationInner = memo(RovoSideNavigationInner, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to `activeMenu` and
// `selectedSpace` via the narrow composite selector so the component only
// re-renders when one of those fields changes, rather than on every UI-store
// state change.
const RovoSideNavigationWithSelector = (props: RovoSideNavigationProps): JSX.Element => {
	di(useSideNavUISelector);
	const [{ activeMenu, selectedSpace }] = useSideNavUISelector();
	return (
		<MemoizedRovoSideNavigationInner
			{...props}
			activeMenu={activeMenu}
			selectedSpace={selectedSpace}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const RovoSideNavigationWithBroadStore = (props: RovoSideNavigationProps): JSX.Element => {
	di(useUIStore);
	const { activeMenu, selectedSpace } = useUIStore();
	return (
		<RovoSideNavigationInner {...props} activeMenu={activeMenu} selectedSpace={selectedSpace} />
	);
};

export function RovoSideNavigation(props: RovoSideNavigationProps): JSX.Element {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<RovoSideNavigationWithSelector {...props} />
	) : (
		<RovoSideNavigationWithBroadStore {...props} />
	);
}
